<template>
  <div>
    <v-dialog
      v-model="isDialogOpen"
      max-width="70%"
      style="z-index: 1017"
      scrollable
      :retain-focus="false"
    >
      <v-overlay v-if="isLoadingDialogOpen">
        <v-progress-circular
          indeterminate
          size="32"
          color="primary"
        ></v-progress-circular>
      </v-overlay>
      <v-card>
        <!-- Título del diálogo -->
        <v-toolbar flat color="white">
          <v-toolbar-title>
            {{ $t("wkt.admin.assessment-review-window.edit_title") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>

        <!-- Contenido principal -->
        <v-card-text>
          <!-- Empleados y Evaluadores -->
          <v-container fluid @wheel="onWheel">
            <v-row>
              <v-col cols="12" sm="6">
                <v-select
                  attach
                  v-model="selectedEmployee"
                  :items="employees"
                  :menu-props="{ maxHeight: '160px', offsetY: true }"
                  :label="$t('wkt.admin.assessment-review-window.employee')"
                  :error-messages="employeeError"
                  clearable
                  :disabled="isEmployeeDisabled"
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="12" sm="6" v-show="showEvaluator">
                <v-autocomplete
                  attach
                  v-model="selectedEvaluator"
                  :items="evaluators"
                  :menu-props="{ maxHeight: '160px', offsetY: true }"
                  :label="$t('wkt.admin.assessment-review-window.evaluator')"
                  :error-messages="evaluatorError"
                  clearable
                  :disabled="isEvaluatorDisabled"
                  item-text="name"
                  item-value="id"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-container>

          <!-- Formulario phase1 -->
          <v-container fluid @wheel="onWheel">
            <v-row v-show="showPhase1Row">
              <v-col cols="12">
                <h3 class="text-h6 mb-2 black--text">
                  {{ $t("wkt.admin.assessment-window.assessment_type1") }}
                </h3>
              </v-col>
            </v-row>
            <v-row v-show="showPhase1Row">
              <v-col :cols="showIcons ? 5 : 6">
                <v-select
                  attach
                  v-model="selectedPhase1Form"
                  :items="forms"
                  :menu-props="{ maxHeight: '160px', offsetY: true }"
                  :label="$t('wkt.admin.news.edit-review-window.phase1_form')"
                  item-text="name"
                  item-value="id"
                  :disabled="isPhase1FormDisabled"
                  dense
                />
              </v-col>
              <v-col v-if="showIcons" cols="1" class="justify-center">
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="blue"
                      v-bind="attrs"
                      v-on="on"
                      @click="openManageFormsDialog"
                    >
                      <v-icon>launch</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("wkt.admin.common.manage_forms") }}</span>
                </v-tooltip>
              </v-col>
              <v-col :cols="showBtnResestPhase1 ? 4 : 6" class="d-flex align-center">
                <v-text-field
                  v-model="phase1CompletionDate"
                  :label="$t('wkt.admin.assessment-review-window.phase2_date_placeholder')"
                  disabled
                  dense
                ></v-text-field>
              </v-col>
              <template v-if="showBtnResestPhase1">
                <v-col cols="2" style="padding: 10px 35px">
                  <v-btn @click="reOpenEvaluation('phase1')" color="primary" :disabled="isReadOnly" text>
                    {{ $t("wkt.admin.assessment.participant-window.button.reopen_evaluation") }}
                  </v-btn>
                </v-col>
              </template>
            </v-row>
          </v-container>

          <!-- Formulario phase2 -->
          <v-container fluid @wheel="onWheel" style="padding-bottom: 130px">
            <v-row v-show="showPhase2Row">
              <v-col cols="12">
                <h3 class="text-h6 mb-2 black--text">
                  {{ $t("wkt.admin.assessment-window.assessment_type2") }}
                </h3>
              </v-col>
            </v-row>

            <v-row v-show="showPhase2Row">
              <v-col :cols="showIcons ? 5 : 6">
                <v-select
                  attach
                  v-model="selectedPhase2Form"
                  :items="forms"
                  :label="$t('wkt.admin.news.edit-review-window.phase2_form')"
                  item-text="name"
                  item-value="id"
                  :disabled="isPhase2FormDisabled"
                  :menu-props="{ maxHeight: '160px', offsetY: true }"
                  dense
                />
              </v-col>
              <v-col v-if="showIcons" cols="1" class="justify-center">
                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      color="blue"
                      v-bind="attrs"
                      v-on="on"
                      @click="openManageFormsDialog"
                    >
                      <v-icon>launch</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("wkt.admin.common.manage_forms") }}</span>
                </v-tooltip>
              </v-col>
              <v-col :cols="showBtnResestPhase2 ? 4 : 6" class="d-flex align-center">
                <v-text-field
                  v-model="phase2CompletionDate"
                  :label="$t('wkt.admin.assessment-review-window.phase2_date_placeholder')"
                  disabled
                  dense
                ></v-text-field>
              </v-col>
              <template v-if="showBtnResestPhase2">
                <v-col cols="1" style="padding: 10px 35px">
                  <v-btn @click="reOpenEvaluation('phase2')" color="primary" :disabled="isReadOnly" text>
                    {{ $t("wkt.admin.assessment.participant-window.button.reopen_evaluation") }}
                  </v-btn>
                </v-col>
              </template>
            </v-row>
          </v-container>
        </v-card-text>

        <!-- Acciones -->
        <v-card-actions>
          <v-btn
            text
            color="white"
            :class="['red darken-1', 'white--text']"
            @click="openConfirmDialog"
            :disabled="isReadOnly"
          >
            <v-icon left>delete</v-icon>
            {{ $t("wkt.admin.common.dialog-buttons.delete") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="saveParticipant" :disabled="isReadOnly">
            <v-icon left>mdi-check</v-icon>
            {{ $t("wkt.admin.common.dialog-buttons.ok") }}
          </v-btn>
          <v-btn text @click="closeDialog">
            <v-icon left>mdi-cancel</v-icon>
            {{ $t("wkt.admin.common.dialog-buttons.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación redirección a formularios -->
    <ConfirmDialog
      ref="dlgConfirmOpenManageForm"
      :title="$t('wkt.admin.common.manage_forms')"
      :message="$t('wkt.admin.common.redirect_to_forms_module')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.ok')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="acceptManageForms()"
      @canceled="closeManageFormsDialog()"
    />

    <!-- Diálogo de Confirmación de borrado -->
    <ConfirmDialog
      ref="dlgConfirmDelete"
      :title="$t('wkt.admin.assessments.edit-review-window.confirm_window.title')"
      :message="$t('wkt.admin.assessments.edit-review-window.confirm_window.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.ok')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteParticipant()"
    />

    <!-- Diálogo de Confirmación de reapertura fase 1 -->
    <ConfirmDialog
      ref="dlgConfirmReOpenPhase1"
      :title="$t('wkt.admin.assessment.reopen_phase1.title')"
      :message="$t('wkt.admin.assessment.reopen_phase1.message')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.ok')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="confirmReopenEvaluation('phase1')"
    />

    <!-- Diálogo de Confirmación de reapertura fase 2 -->
    <ConfirmDialog
      ref="dlgConfirmReOpenPhase2"
      :title="$t('wkt.admin.assessment.reopen_phase2.title')"
      :message="$t('wkt.admin.assessment.reopen_phase2.message')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.ok')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="confirmReopenEvaluation('phase2')"
    />
  </div>
</template>

<script>
import gwt from "../../../api/gwt.js";
import api from "../../../api/worktime";
import { Constants } from "../../common/Constants.js";
import ParticipantsDialog from "./ParticipantsDialog.vue";
import ConfirmDialog from "../../common/ConfirmDialog.vue";

export default {
  name: "AddParticipantsDialog",
  components: {
    ConfirmDialog,
  },

  data() {
    return {
      currentAssesment: [],
      review: [],
      isLoadingConfirmDialogOpen: false,
      isLoadingDialogOpen: false,
      isLoading: false,
      isConfirmDialogOpen: false,
      selectedEmployee: null,
      selectedEvaluator: null,
      selectedPhase1Form: null,
      selectedPhase2Form: null,
      employees: [],
      evaluators: [],
      showEvaluator: false,
      showPhase1Row: false,
      showPhase2Row: false,
      isEmployeeDisabled: false,
      isEvaluatorDisabled: false,
      isPhase1FormDisabled: false,
      isPhase2FormDisabled: false,
      phase1CompletionDate: null,
      phase2CompletionDate: null,
      assessmentType: null,
      btnDelete: true,
      assessment: null,
      isDialogOpen: false,
      isManageFormsDialogOpen: false,
      employeeError: null,
      evaluatorError: null,
      forms: [],
      profile: null,
      showIcons: false,
      phaseToReopen: null,
      phase1DueDate:null,
      phase2DueDate:null
    };
  },
  computed: {
    showBtnResestPhase1() {
      if (this.phase1DueDate > Date.now()) {
        return !!this.phase1CompletionDate;
      }
      return false;
    },
    showBtnResestPhase2() {
      if (this.phase2DueDate > Date.now()) {
        return !!this.phase2CompletionDate;
      }
      return false;
    },
    sessionModuleAssessments() {
      return this.profile?.moduleAssessments;
    },
    userHasAnyAccess() {
      const mp = this.sessionModuleAssessments;
      return !(mp === Constants.DENY_ALL);
    },
    isReadOnly() {
      return (this.sessionModuleAssessments === Constants.ALLOW_READ_ONLY);
    },
  },
  methods: {
    open(review, currentAssesment) {
      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", { text: this.$t("wkt.admin.common.error.try_again_later"), color: "error", timeout: 3000 });
        return;
      }
      this.review = review;
      this.currentAssesment = currentAssesment;
      this.isDialogOpen = true;
      this.assessment = currentAssesment;
      this.assessmentType = currentAssesment.assessmentType;
      this.phase1DueDate = currentAssesment.phase1DueDate;
      this.phase2DueDate = currentAssesment.phase2DueDate;
      // Limpiar los selects
      this.selectedPhase1Form = null;
      this.selectedPhase2Form = null;
      this.selectedEmployee = null;
      this.selectedEvaluator = null;
      this.doEdit();
      this.setData();
    },
    doCreate() {
      this.windowTitle = this.$t(
        "wkt.admin.assessment-review-window.new_title"
      );
      this.isEmployeeDisabled = false;
      this.isEvaluatorDisabled = false;
      this.isPhase1FormDisabled = false;
      this.isPhase2FormDisabled = false;
      this.selectedPhase1Form = -1;
      this.selectedPhase2Form = -1;
      this.selectedEmployee = -1;
      this.selectedEvaluator = -1;
    },

    doEdit() {
      this.showDeleteButton = true;
      this.windowTitle = this.$t(
        "wkt.admin.assessment-review-window.edit_title"
      );
      this.isEmployeeDisabled = true;

      // Deshabilitar campos según las fechas de finalización
      this.isEvaluatorDisabled = !!(
        this.review && this.review.phase2CompletionDate
      );
      this.isPhase1FormDisabled = !!(
        this.review && this.review.phase1CompletionDate
      );
      this.isPhase2FormDisabled = !!(
        this.review && this.review.phase2CompletionDate
      );

      // Mostrar fechas de finalización si existen
      this.phase1CompletionDate =
        this.review && this.review.phase1CompletionDate
          ? this.formatDate(this.review.phase1CompletionDate)
          : "";
      this.phase2CompletionDate =
        this.review && this.review.phase2CompletionDate
          ? this.formatDate(this.review.phase2CompletionDate)
          : "";

      if (this.review) {
        this.selectedPhase1Form =
          this.review.phase1FormId != null ? this.review.phase1FormId : -1;
        this.selectedPhase2Form =
          this.review.phase2FormId != null ? this.review.phase2FormId : -1;
        this.selectedEmployee = this.review.employeeId || null;
        this.selectedEvaluator = this.review?.evaluatorId ?? -1;
      } else {
        this.selectedPhase1Form = -1;
        this.selectedPhase2Form = -2;
        this.selectedEmployee = null;
        this.selectedEvaluator = null;
      }
    },

    setData() {
      this.getEmployees();
      this.getForms();

      // Establecer visibilidad de iconos
      this.profile = gwt.getSessionProfile();
      this.showIcons = this.profile.modulePForms === Constants.ALLOW_READ_WRITE;

      // Ajustar visibilidad según el tipo de evaluación
      this.showEvaluator =
        this.assessmentType === 1 || this.assessmentType === 2;
      this.showPhase1Row =
        this.assessmentType === 0 || this.assessmentType === 2;
      this.showPhase2Row =
        this.assessmentType === 1 || this.assessmentType === 2;
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const locale = navigator.language || "en";
      const options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      };
      return d.toLocaleString(locale, options);
    },
    getEmployees() {
      this.allEmployees = [];
      this.employees = [];
      this.evaluators = [];

      const departments = gwt.getDepartments();
      this.allEmployees = departments.flatMap((dept) => dept.employees);

      const mappedEmployees = this.allEmployees.map((emp) => ({
        id: emp.id,
        name: `${emp.lastname}, ${emp.firstname}`,
      }));

      // Lista para empleados
      this.employees = [...mappedEmployees];
      this.employees.unshift({
        id: -1,
        name: ` - ${this.$t(
          "wkt.admin.assessment-review-window.select_none_employee"
        )} - `,
      });

      // Lista para evaluadores
      this.evaluators = [...mappedEmployees];
      this.evaluators.unshift({
        id: -1,
        name: ` - ${this.$t("wkt.admin.assessment-review-window.select_none_evaluator")} - `
      });
    },
    getForms() {
      if (!Array.isArray(this.forms)) {
        this.forms = [];
      }

      this.forms.unshift({
        id: -1,
        name: ` - ${this.$t("wkt.admin.assessment-review-window.select_none_form")} - `
      });

      api.getForms(0, 100, "", "", false,
        (data) => {
          this.forms = [
            ...this.forms,
            ...data.entities.map((form) => ({
              id: form.id,
              name: form.name,
            })),
          ];
        },
        (err) => {
          console.error("Error al obtener los formularios:", err);
        }
      );
    },
    openManageFormsDialog() {
      this.$refs.dlgConfirmOpenManageForm.confirm();
    },
    closeManageFormsDialog() {
      this.isManageFormsDialogOpen = false;
    },
    acceptManageForms() {
      gwt.fireEvent("OpenFormsEvent");
      this.closeManageFormsDialog();
    },
    validate() {
      this.employeeError = this.selectedEmployee ? null : this.$t("wkt.admin.common.select_employees_required");

      if (this.assessmentType === 1 || this.assessmentType === 2) {
        this.evaluatorError = this.selectedEvaluator ? null : this.$t("wkt.admin.assessment-review-window.select_none_evaluator");
        if (this.selectedEvaluator === this.selectedEmployee) {
          this.evaluatorError = this.$t("wkt.admin.assessment-review-window.employee_and_evaluator_are_same");
        }
      }

      return !this.employeeError && !this.evaluatorError;
    },
    saveParticipant() {
      if (!this.validate()) {
        return;
      }
      this.isLoadingDialogOpen = true;

      this.review = {
        ...this.review,
        employeeId: this.selectedEmployee === -1 ? null : this.selectedEmployee,
      };
      if (this.assessmentType === 0 || this.assessmentType === 2) {
        this.review.phase1FormId = this.selectedPhase1Form === -1 ? null : this.selectedPhase1Form;
      }

      if (this.assessmentType === 1 || this.assessmentType === 2) {
        this.review.evaluatorId = this.selectedEvaluator === -1 ? null : this.selectedEvaluator;
        this.review.phase2FormId = this.selectedPhase2Form === -1 ? null : this.selectedPhase2Form;
      }

      api.storeAssessmentReview(this.review,
        (response) => {
          this.isLoading = false;
          console.log("Respuesta de la API:", response);
          this.closeDialog();
          this.$emit("assessment-review-updated", response);
          this.isLoadingDialogOpen = false;
        },
        (error) => {
          console.error("Error al guardar el participante:", error);
          this.isLoadingDialogOpen = false;
        }
      );
    },
    deleteParticipant() {
      this.isLoadingConfirmDialogOpen = true;

      api.deleteAssessmentReviewById(this.currentAssesment.id,
        () => {
          this.isLoadingConfirmDialogOpen = false;
          this.closeConfirmDialog();
          this.closeDialog();
          ParticipantsDialog;
          this.$emit("participant-deleted");
        },
        (error) => {
          console.error("Error al eliminar el participante:", error);
          this.isLoadingConfirmDialogOpen = true;
          this.closeConfirmDialog();
        }
      );
    },
    closeDialog() {
      this.isDialogOpen = false;
    },
    openConfirmDialog() {
      this.$refs.dlgConfirmDelete.confirm();
    },
    closeConfirmDialog() {
      this.isConfirmDialogOpen = false;
    },
    onWheel(event) {
      event.stopPropagation();
    },
    reOpenEvaluation(phase) {
      if (phase === 'phase1') {
        this.$refs.dlgConfirmReOpenPhase1.confirm();
      } else if (phase === 'phase2') {
        this.$refs.dlgConfirmReOpenPhase2.confirm();
      }
    },
    confirmReopenEvaluation(phase) {
      if (phase === 'phase1') {
        this.$set(this.review, "phase1CompletionDate", null);
        this.phase1CompletionDate = "";
        // Resetear también phase2 si es necesario
        if (this.assessmentType === 2 && phase === 'phase1') {
          this.$set(this.review, "phase1CompletionDate", null);
          this.phase1CompletionDate = "";
          this.$set(this.review, "phase2CompletionDate", null);
          this.phase2CompletionDate = "";
        }
        this.$set(this.review, "reviewStatus", 0);
      } if (phase === 'phase2' && this.assessmentType === 2) {
        this.$set(this.review, "phase2CompletionDate", null);
        this.phase2CompletionDate = "";
        this.$set(this.review, "reviewStatus", 1);
      } else if (phase === 'phase2') {
        this.$set(this.review, "phase2CompletionDate", null);
        this.phase2CompletionDate = "";
        this.$set(this.review, "reviewStatus", 0);
      }

      this.doEdit();
    }
  },
};
</script>

<style scoped>
.clickable {
  cursor: pointer;
}

.v-btn {
  margin: 8px 0;
}
</style>